import { Autocomplete, Tooltip, Typography } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { AppCheckbox } from 'src/feature/form/component/analog/AppCheckbox'
import { UsedSkillMetrics } from '../../model/my-team-skill-gaps'
import { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

const ICON_UNCHECKED = <CheckBoxOutlineBlankIcon fontSize="small" />
const ICON_CHECKED = <CheckBoxIcon fontSize="small" />
const LOAD_MORE_ID = '__load_more__'
const SELECT_ALL_ID = '__select_all__'

export type SkillSelectorProps = {
  selectedSkills: UsedSkillMetrics[]
  allSkills: UsedSkillMetrics[]
  totalCount: number
  hasMore: boolean
  inputValue: string
  isLoading: boolean
  isInitialLoad: boolean
  onSkillsChange: (skills: UsedSkillMetrics[]) => void
  onInputChange: (event: any, value: string) => void
  onLoadMore: () => void
}

export const SkillSelector = observer(({ selectedSkills, allSkills, totalCount, hasMore, inputValue, isLoading, isInitialLoad, onSkillsChange, onInputChange, onLoadMore }: SkillSelectorProps) => {
  const { translation } = useLocalizationStore()
  
  const pageTranslation = translation.pages.myTeam.skillGapInsights
  const sharedSkillHint = translation.pages.myTeam.metricsHeader.sharedSkillHint()
  
  const handleSelectAll = () => {
    const allValidSkills = allSkills.filter(skill => skill.setName !== LOAD_MORE_ID && skill.setName !== SELECT_ALL_ID)
    onSkillsChange(allValidSkills)
  }

  const handleChange = (_: any, newValue: UsedSkillMetrics[]) => {
    const hasSelectAll = newValue.some(v => v.setName === SELECT_ALL_ID)

    if (hasSelectAll) {
      handleSelectAll()

      return
    }

    const filtered = newValue.filter(v => v.setName !== LOAD_MORE_ID && v.setName !== SELECT_ALL_ID)
    onSkillsChange(filtered)
  }

  const optionsWithLoadMore = useMemo(() => {
    const selectAllOption = { ids: [SELECT_ALL_ID], setName: SELECT_ALL_ID } as UsedSkillMetrics
    const loadMoreOption = { ids: [LOAD_MORE_ID], setName: LOAD_MORE_ID } as UsedSkillMetrics

    return hasMore ? [selectAllOption, ...allSkills, loadMoreOption] : [selectAllOption, ...allSkills]
  }, [allSkills, hasMore])

  const getOptionLabel = (option: UsedSkillMetrics) => option.setName

  const isOptionEqualToValue = (option: UsedSkillMetrics, value: UsedSkillMetrics) => {
    const optionIds = option.ids
      .slice()
      .sort()
      .join(',')
    const valueIds = value.ids
      .slice()
      .sort()
      .join(',')

    return optionIds === valueIds
  }

  const renderOption = useCallback((props: any, option: UsedSkillMetrics, { selected }: any) => {
    if (option.setName === SELECT_ALL_ID) {
      const allValidSkills = allSkills.filter(skill => skill.setName !== LOAD_MORE_ID && skill.setName !== SELECT_ALL_ID)
      const isAllSelected = selectedSkills.length === allValidSkills.length && allValidSkills.length > 0

      return (
        <li {...props}>
          <AppCheckbox
            icon={ICON_UNCHECKED}
            checkedIcon={ICON_CHECKED}
            checked={isAllSelected}
            size="small"
          />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
            {pageTranslation.selectAll()} ({allValidSkills.length})
          </Typography>
        </li>
      )
    }

    if (option.setName === LOAD_MORE_ID) {
      const remainingCount = totalCount - allSkills.length

      return (
        <li
          {...props}
          onClick={(e) => {
            e.stopPropagation()
            onLoadMore()
          }}
          style={styles.loadMoreOption}
        >
          <Typography
            sx={{
              fontSize: 11,
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            +{remainingCount} {pageTranslation.more()}
          </Typography>
        </li>
      )
    }

    return (
      <li {...props}>
        <AppCheckbox
          icon={ICON_UNCHECKED}
          checkedIcon={ICON_CHECKED}
          checked={selected}
          size="small"
        />
        {(option.ids ?? []).length > 1 && (
          <Tooltip title={sharedSkillHint} placement="top" arrow>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'secondary.main', mr: 2 }} />
          </Tooltip>
        )}
        <Typography sx={{ fontSize: 12 }}>{option.setName}</Typography>
      </li>
    )
  }, [allSkills, selectedSkills.length, totalCount, onLoadMore, sharedSkillHint])

  const renderInput = (params: any) => (
    <AppTextField
      {...params}
      placeholder={pageTranslation.searchSkillsPlaceholder()}
      sx={styles.textField}
    />
  )

  const renderTags = (value: UsedSkillMetrics[]) => {
    if (value.length === 0) {
      return null
    }

    return (
      <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 1 }}>
        {value.length} {value.length === 1 ? pageTranslation.skillSelected() : pageTranslation.skillsSelected()} {pageTranslation.selected()}
      </Typography>
    )
  }

  return (
    <AppBox sx={styles.filterSection}>
      <Caption sx={styles.filterLabel}>
        {pageTranslation.selectSkills()} ({selectedSkills.length})
      </Caption>
      {isLoading && isInitialLoad ? (
        <AppSkeleton
          count={1}
          direction="column"
          rects={[{ width: '100%', height: 40 }]}
        />
      ) : (
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={optionsWithLoadMore}
          value={selectedSkills}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={onInputChange}
          loading={isLoading}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          renderOption={renderOption}
          renderInput={renderInput}
          renderTags={renderTags}
          sx={styles.autocomplete}
        />
      )}
    </AppBox>
  )
})

const styles = {
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: '100%',
  },
  filterLabel: {
    fontSize: 11,
    color: 'text.secondary',
  },
  textField: {
    fontSize: 12,
    m: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.dark',
      borderRadius: '10px',
    },
  },
  autocomplete: {
    width: '100%',
    backgroundColor: 'background.dark',
    borderRadius: '10px',
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      color: 'text.primary',
    },
    '& .MuiChip-label': {
      fontSize: 12,
    },
  },
  loadMoreOption: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 16px',
  },
}
